/**
 * MasterclassCountdown — full-screen takeover when a coach taps a
 * pre-release masterclass card. Renders the cover image, title,
 * description, key points, and a live-ticking countdown to availableAt.
 * The countdown unmounts itself the moment the timer hits zero and
 * calls onUnlock() so the parent can flip into the reader without a
 * page refresh.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Bell, Calendar } from "lucide-react";
import { GOLD } from "../constants/theme";
import { coverSources } from "./coverArt";

const hf = "'Bebas Neue',sans-serif";

export function MasterclassCountdown({ item, module: mod, C, dark, onClose, onUnlock }) {
  // A dateless "coming soon" item (no availableAt) shows the takeover
  // without a live clock or a drop date — it just isn't scheduled yet.
  const hasDate = !!item.availableAt && !Number.isNaN(new Date(item.availableAt).getTime());
  const target = hasDate ? new Date(item.availableAt).getTime() : 0;
  const [remaining, setRemaining] = useState(() => hasDate ? Math.max(0, target - Date.now()) : 0);
  const unlockedRef = useRef(false);

  // Tick every second. Once the timer hits zero, fire onUnlock once
  // (the ref guards against the parent re-rendering us before it
  // unmounts on its own). Skipped entirely for dateless items.
  useEffect(() => {
    if (!hasDate) return undefined;
    const id = setInterval(() => {
      const r = Math.max(0, target - Date.now());
      setRemaining(r);
      if (r === 0 && !unlockedRef.current) {
        unlockedRef.current = true;
        onUnlock?.();
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target, onUnlock, hasDate]);

  // Esc closes the takeover.
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Body scroll lock while the takeover is open. Same pattern other
  // modal-style surfaces use; restored on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const days    = Math.floor(remaining / 86400000);
  const hours   = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  const [coverIdx, setCoverIdx] = useState(0);

  const dropDate = hasDate ? new Date(item.availableAt) : null;
  const dropFormatted = hasDate ? dropDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : null;
  const dropTime = hasDate ? dropDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : null;

  // Same order as the shelf card, so what a coach clicked is what they get:
  // a real photo, else a frame from the class's own video, else the
  // generated art. See masterclass/coverArt.js.
  // Advance on error, like the shelf card. This is a full-screen takeover,
  // so a playback id that 404s would leave a blank hero rather than a
  // slightly worse picture.
  const covers = coverSources(item, mod, { width: 1280, height: 720 });
  const cover = covers[coverIdx] || null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={hasDate ? `${item.title} drops ${dropFormatted}` : `${item.title} coming soon`}
      style={{
        position:"fixed", inset:0, zIndex:6000,
        background:"#070707", color:"#F5F5F5",
        display:"flex", flexDirection:"column",
        fontFamily:"'Inter','DM Sans',sans-serif",
        overflow:"auto",
      }}
    >
      {/* Stadium spotlight gradient (top center) + court watermark
          backdrop, same recipe as Film Studio so the takeover feels like
          part of the same visual world. */}
      <div aria-hidden="true" style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:0,
        background:`radial-gradient(ellipse 75% 60% at 50% -5%, ${GOLD}33 0%, ${GOLD}1A 25%, ${GOLD}08 50%, transparent 75%)`}}/>
      <CountdownCourtBackdrop/>
      <div aria-hidden="true" className="mc-countdown-sweep" style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:0}}/>

      <style>{`
        @keyframes mcCountdownSweep { 0% { transform: translateX(-110%); } 100% { transform: translateX(110%); } }
        .mc-countdown-sweep {
          background: linear-gradient(110deg, transparent 30%, ${GOLD}10 50%, transparent 70%);
          mix-blend-mode: screen;
          animation: mcCountdownSweep 14s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) { .mc-countdown-sweep { animation: none; opacity: 0; } }
        @keyframes mcCountdownPulse { 0%,100% { opacity: .85; } 50% { opacity: 1; } }
        .mc-countdown-tick { animation: mcCountdownPulse 1s ease-in-out infinite; }
        @keyframes mcFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .mc-countdown-content { animation: mcFadeIn .35s ease-out both; }
      `}</style>

      {/* Close button */}
      <button onClick={onClose} aria-label="Close"
        style={{
          position:"fixed", top:18, right:20, zIndex:2,
          background:"rgba(0,0,0,0.45)", border:"1px solid rgba(255,255,255,0.12)",
          color:"#F5F5F5", borderRadius:10, padding:"7px 9px",
          cursor:"pointer", display:"flex", alignItems:"center",
        }}>
        <X size={16}/>
      </button>

      <div className="mc-countdown-content" style={{
        position:"relative", zIndex:1, flex:1,
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"60px 24px",
      }}>
        <div style={{maxWidth:920, width:"100%", textAlign:"center"}}>

          {/* Eyebrow */}
          <div style={{display:"inline-flex", alignItems:"center", gap:8,
            padding:"6px 12px", borderRadius:999,
            background:`${GOLD}15`, border:`1px solid ${GOLD}40`,
            fontSize:10, fontWeight:800, color:GOLD, letterSpacing:1.8, textTransform:"uppercase",
            marginBottom:20,
          }}>
            <Calendar size={12}/> Coming Soon
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily:hf,
            fontSize:"clamp(40px, 6vw, 72px)",
            letterSpacing:2,
            lineHeight:0.95,
            color:"#F5F5F5",
            margin:"0 0 14px",
            textShadow:`0 4px 22px rgba(0,0,0,0.6), 0 0 50px ${GOLD}28`,
          }}>{(item.title || mod?.title || "").toUpperCase()}</h1>

          {/* Sub */}
          {(item.sub || mod?.sub) && (
            <div style={{fontSize:16, color:"rgba(245,245,245,0.78)", lineHeight:1.55, maxWidth:680, margin:"0 auto 36px"}}>
              {item.sub || mod?.sub}
            </div>
          )}

          {/* Cover */}
          {cover && (
            <div style={{
              maxWidth:680, margin:"0 auto 36px",
              borderRadius:14, overflow:"hidden",
              border:`1px solid rgba(255,255,255,0.08)`,
              boxShadow:`0 22px 60px rgba(0,0,0,0.6), 0 0 0 1px ${GOLD}22`,
              aspectRatio:"16/9",
              background:"#000",
              position:"relative",
            }}>
              <img src={cover} alt="" loading="lazy"
                onError={() => setCoverIdx((i) => i + 1)}
                style={{width:"100%", height:"100%", objectFit:"cover", filter:"brightness(0.65) saturate(0.9)"}}/>
              <div style={{position:"absolute", inset:0, background:"linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)"}}/>
            </div>
          )}

          {/* Countdown clock + drop time — only when a release date is set.
              Dateless "coming soon" items skip both and just show the teaser. */}
          {hasDate ? (
            <>
              <div style={{display:"inline-flex", gap:14, marginBottom:28, flexWrap:"wrap", justifyContent:"center"}}>
                <CountdownCell label="Days"    value={days}/>
                <CountdownCell label="Hours"   value={hours}/>
                <CountdownCell label="Minutes" value={minutes}/>
                <CountdownCell label="Seconds" value={seconds} pulse/>
              </div>

              {/* Drop time */}
              <div style={{fontSize:13, color:"rgba(245,245,245,0.65)", marginBottom:32, lineHeight:1.55}}>
                Drops <strong style={{color:GOLD}}>{dropFormatted}</strong> at {dropTime}
              </div>
            </>
          ) : (
            <div style={{fontSize:14, color:"rgba(245,245,245,0.7)", marginBottom:32, lineHeight:1.55}}>
              <strong style={{color:GOLD}}>Coming soon.</strong> A release date has not been set yet.
            </div>
          )}

          {/* Key points teaser */}
          {Array.isArray(item.keyPoints) && item.keyPoints.length > 0 && (
            <div style={{maxWidth:560, margin:"0 auto 28px", textAlign:"left"}}>
              <div style={{fontSize:10, fontWeight:800, color:GOLD, letterSpacing:1.8, textTransform:"uppercase", marginBottom:10}}>
                What's inside
              </div>
              <ul style={{margin:0, paddingLeft:20}}>
                {item.keyPoints.map((kp, i) => (
                  <li key={i} style={{fontSize:14, lineHeight:1.65, color:"rgba(245,245,245,0.82)", marginBottom:6}}>{kp}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Soft "we'll let you know" line — no actual subscribe action
              wired here yet; the bell is a visual signal that the platform
              tracks new releases. Hook into in-app notifications later. */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:8,
            fontSize:12, color:"rgba(245,245,245,0.55)",
            marginTop:6,
          }}>
            <Bell size={13} color={GOLD}/> We'll surface this on your dashboard the moment it drops.
          </div>
        </div>
      </div>
    </div>
  );
}

// CountdownCell — one slot of the timer (days, hours, minutes, seconds).
// JetBrains Mono for the digits so they don't shift width as numbers
// tick. Pulse animation is opt-in for the seconds cell so the whole
// timer doesn't look anxious.
function CountdownCell({ label, value, pulse }) {
  const display = String(value).padStart(2, "0");
  return (
    <div style={{
      minWidth:84, padding:"14px 16px",
      borderRadius:12,
      background:"rgba(255,255,255,0.04)",
      border:`1px solid rgba(255,255,255,0.10)`,
      boxShadow:`inset 0 0 30px rgba(0,0,0,0.4)`,
    }}>
      <div className={pulse ? "mc-countdown-tick" : ""}
        style={{
          fontFamily:"'JetBrains Mono', 'Bebas Neue', monospace",
          fontSize:42, fontWeight:700,
          color:"#F5F5F5", lineHeight:1,
          textShadow:`0 0 22px ${GOLD}33`,
        }}>{display}</div>
      <div style={{
        fontSize:9, fontWeight:800, color:"rgba(245,245,245,0.5)",
        letterSpacing:1.6, textTransform:"uppercase", marginTop:8,
      }}>{label}</div>
    </div>
  );
}

// CountdownCourtBackdrop — same half-court SVG the Film Studio shell
// uses, scaled down for the countdown surface. ~3% gold opacity.
function CountdownCourtBackdrop() {
  return (
    <div aria-hidden="true" style={{position:"absolute", inset:0, pointerEvents:"none", zIndex:0,
      display:"flex", alignItems:"center", justifyContent:"center"}}>
      <svg viewBox="0 0 100 50" preserveAspectRatio="xMidYMid meet"
        style={{width:"min(90vw, 1100px)", height:"auto", opacity:0.035, color:GOLD, maxHeight:"75vh"}}>
        <rect x="0" y="0" width="100" height="50" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <path d="M 30 0 A 20 20 0 0 0 70 0" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <rect x="35" y="40" width="30" height="10" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <circle cx="50" cy="40" r="6" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <path d="M 22 50 A 28 28 0 0 1 78 50" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <path d="M 46 50 A 4 4 0 0 1 54 50" fill="none" stroke="currentColor" strokeWidth=".22"/>
        <line x1="44" y1="49" x2="56" y2="49" stroke="currentColor" strokeWidth=".3"/>
        <circle cx="50" cy="48.2" r=".9" fill="none" stroke="currentColor" strokeWidth=".22"/>
      </svg>
    </div>
  );
}
